import { type CutoffTime, type Day } from '@choco-admin/types';
import { FC, useMemo } from 'react';

import { ChocoCheckbox, ChocoSelect } from '../FormComponents';

import { generateTimeOptions } from './constants';

interface DeliveryDaySettingsProps {
  day: Day;
  isEnabled: boolean;
  cutoffTime: CutoffTime;
  cutoffDay: string;
  onToggle: (day: Day) => void;
  onCutoffTimeChange: (day: Day, time: CutoffTime) => void;
  onCutoffDayChange: (day: Day, cutoffDay: string) => void;
  errorMessage?: string;
  cutoffType: 'no_cutoff' | 'strict' | 'flexible';
}

export const DeliveryDaySettings: FC<DeliveryDaySettingsProps> = ({
  day,
  isEnabled,
  cutoffTime,
  cutoffDay,
  onToggle,
  onCutoffTimeChange,
  onCutoffDayChange,
  errorMessage,
  cutoffType,
}) => {
  const timeOptions = useMemo(() => generateTimeOptions(), []);
  const dayOptions = useMemo(
    () =>
      Array.from({ length: 6 }, (_, i) => ({
        value: String(i),
        label: i === 0 ? 'Same day' : `${i} day${i === 1 ? '' : 's'} before`,
      })),
    []
  );

  const handleChange = () => {
    onToggle(day);
  };

  // Remove :59:59 from cutoff time if present
  const displayCutoffTime = cutoffTime?.replace?.(':59:59', ':59') || '23:59';

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3 md:items-start md:gap-4">
      <div
        className={`flex min-h-10 items-center ${
          cutoffType === 'no_cutoff' ? 'md:col-span-3' : ''
        }`}
      >
        <ChocoCheckbox
          isSelected={isEnabled}
          onValueChange={handleChange}
          size="sm"
          color="primary"
          label={day}
        />
      </div>
      {cutoffType !== 'no_cutoff' && (
        <>
          <div className="space-y-1">
            <span className="block text-xs font-medium text-default-600 md:hidden">
              Cutoff day
            </span>
            <ChocoSelect
              value={cutoffDay}
              onChange={(e) => onCutoffDayChange(day, e.target.value)}
              options={dayOptions}
              className="w-full"
              aria-label={`Cutoff day for ${day}`}
              isDisabled={!isEnabled}
            />
          </div>
          <div className="space-y-1">
            <span className="block text-xs font-medium text-default-600 md:hidden">
              Cutoff time
            </span>
            <ChocoSelect
              value={displayCutoffTime}
              onChange={(e) =>
                onCutoffTimeChange(day, e.target.value as CutoffTime)
              }
              options={timeOptions}
              className="w-full"
              aria-label={`Cutoff time for ${day}`}
              isDisabled={!isEnabled}
              errorMessage={errorMessage}
            />
          </div>
        </>
      )}
    </div>
  );
};
